import React from 'react';
import { formatDate, UTCYesterday } from 'shared/src/utils/date/date-utils';
import { formatCurrency } from '@/components/tables/table-utils';
import {
  getElapsedDaysDisplay,
  getRemainingDaysDisplay,
  getUnitName
} from 'shared/src/domain/line-item/line-item-utils';
import { Calendar } from '@/icons/calendar';
import { Truck } from '@/icons/truck';
import { MoneySpent } from '@/icons/moneySpent';
import clsx from 'clsx';
import { UTCDate } from '@date-fns/utc';
import { diffInDays } from '@/metrics/calculate-metrics';
import { UnitPriceType } from 'shared/src/domain/line-item/line-item-channels';
import { isNil } from 'lodash';
import { isAfter, isBefore } from 'date-fns';
import { CampaignMetrics, LineItemMetricV2 } from 'shared/src/domain/metrics/metrics-types';
import { ExecutionLineItemTrpcResponse } from 'shared/src/trpc-types/line-item-trpc-types';
import { StrategyTrpcResponse } from 'shared/src/trpc-types/strategy-trpc-types';
import { toUTCDate } from 'shared/src/date-string-utils/date-utils';
import { ISODateString } from 'shared/src/date-string-utils/types';

type CampaignPerformanceRailV2Props = {
  strategy: StrategyTrpcResponse;
  metrics: CampaignMetrics;
};

export function CampaignPerformanceRailV2({ metrics, strategy }: CampaignPerformanceRailV2Props) {
  const { actual_media_spend, planned_spend, effective_billable_revenue } = metrics;
  const showEffectivePrice =
    strategy.referral_commission_rate && strategy.referral_commission_rate > 0;

  return (
    <div className="flex flex-col gap-1 rounded-md bg-gray-100 p-1">
      <DatesElapsedSection startDate={strategy.start_date} endDate={strategy.end_date} />
      <PriceSection actual={effective_billable_revenue} target={strategy.price || 0} />
      <SpendSection actual={actual_media_spend} target={planned_spend} />
      {showEffectivePrice ? (
        <EffectivePriceSectionV2
          deliveredPrice={effective_billable_revenue}
          expectedPrice={strategy.effective_price || strategy.price || 0}
        />
      ) : null}
    </div>
  );
}

type LineItemPerformanceRailV2Props = {
  lineItem: ExecutionLineItemTrpcResponse & { unit_price_type: UnitPriceType };
  metrics: LineItemMetricV2;
};

export function LineItemPerformanceRailV2({ lineItem, metrics }: LineItemPerformanceRailV2Props) {
  const { actual_delivered_units } = metrics;

  return (
    <div className="flex flex-col gap-1 rounded-md bg-gray-100 p-1">
      <DatesElapsedSection startDate={lineItem.dates.from} endDate={lineItem.dates.to} />
      <UnitsDeliveredSection
        actual={actual_delivered_units}
        target={lineItem.estimated_units || 0}
        unitPriceType={lineItem.unit_price_type}
      />
      <SpendSection actual={metrics.actual_media_spend} target={lineItem.media_budget} />
      <UnitCostSection
        unitPriceType={lineItem.unit_price_type}
        actual={metrics.unit_cost_actual}
        target={lineItem.planned_unit_cost || 0}
      />
    </div>
  );
}

function DatesElapsedSection({
  startDate,
  endDate
}: {
  startDate: ISODateString | undefined | null;
  endDate: ISODateString | undefined | null;
}) {
  const startUTC = startDate ? toUTCDate(startDate) : null;
  const endUTC = endDate ? toUTCDate(endDate) : null;
  const pct = calcElapsedProportion(startUTC, endUTC);
  const elapsedDays = startUTC ? diffInDays(startUTC, UTCYesterday()) : 0;
  const lengthInDays = startUTC && endUTC ? diffInDays(startUTC, endUTC) : 0;
  return (
    <Section>
      <SectionHeader percentage={pct} label="of schedule elapsed" />
      <div className="flex items-center justify-between rounded-sm border border-[#dfe4ea] px-1.5 py-0.5 text-xs text-gray-700">
        <div>{formatDate(startUTC, 'M-dd-yy')}</div>-<div>{formatDate(endUTC, 'M-dd-yy')}</div>
      </div>
      <BarRow>
        <Calendar />
        <div className="progress-bar-shadow w-full rounded-xl bg-gray-100">
          <Bar percentage={pct} />
        </div>
      </BarRow>
      <div className="flex flex-col gap-0.5">
        <Metric
          label="Days elapsed"
          value={getElapsedDaysDisplay(elapsedDays, lengthInDays)}
          colorClass="text-gray-800"
        />
        <Metric
          label="Total duration"
          value={lengthInDays.toLocaleString()}
          colorClass="text-gray-500"
        />
        <Metric
          label="Remaining"
          value={getRemainingDaysDisplay(elapsedDays, lengthInDays)}
          colorClass="text-gray-500"
        />
      </div>
    </Section>
  );
}

function UnitsDeliveredSection({
  actual,
  target,
  unitPriceType
}: {
  actual: number;
  target: number;
  unitPriceType: UnitPriceType;
}) {
  const { full: fullUnitName } = getUnitName(unitPriceType.name);
  const pct = calcProportion(actual, target);
  return (
    <Section>
      <SectionHeader
        percentage={pct}
        label={`of ${getUnitName(unitPriceType.name).full} delivered`}
      />
      <BarRow>
        <Truck />
        <div className="progress-bar-shadow w-full rounded-xl bg-gray-100">
          <Bar percentage={pct} />
        </div>
      </BarRow>
      <div className="flex flex-col gap-0.5">
        <Metric
          label={`${fullUnitName.charAt(0).toUpperCase()}${fullUnitName.substring(1)} delivered`}
          value={actual === 0 ? '— —' : actual.toLocaleString()}
          colorClass="text-gray-800"
        />
        <Metric label="Expected" value={target.toLocaleString()} colorClass="text-gray-500" />
        <Metric
          label="Remaining"
          value={actual === 0 ? '— —' : (target - actual).toLocaleString()}
          colorClass="text-gray-500"
        />
      </div>
    </Section>
  );
}

function PriceSection({ actual, target }: { actual: number; target: number }) {
  const pct = calcProportion(actual, target);
  return (
    <Section>
      <SectionHeader percentage={pct} label="of price delivered" />
      <BarRow>
        <Truck />
        <div className="progress-bar-shadow w-full rounded-xl bg-gray-100">
          <Bar percentage={pct} />
        </div>
      </BarRow>
      <div className="flex flex-col gap-0.5">
        <Metric
          label="Price delivered"
          value={actual === 0 ? '— —' : formatCurrency(actual)}
          colorClass="text-gray-800"
        />
        <Metric label="Expected" value={formatCurrency(target)} colorClass="text-gray-500" />
        <Metric
          label="Remaining"
          value={actual === 0 ? '— —' : formatCurrency(target - actual)}
          colorClass="text-gray-500"
        />
      </div>
    </Section>
  );
}

function SpendSection({ actual, target }: { actual: number; target: number }) {
  const pct = calcProportion(actual, target);
  return (
    <Section>
      <SectionHeader percentage={pct} label="of media budget spent" />
      <BarRow>
        <MoneySpent />
        <div className="progress-bar-shadow w-full rounded-xl bg-gray-100">
          <Bar percentage={pct} />
        </div>
      </BarRow>
      <div className="flex flex-col gap-0.5">
        <Metric
          label="Spent"
          value={actual === 0 ? '— —' : formatCurrency(actual)}
          colorClass="text-gray-800"
        />
        <Metric label="Budget" value={formatCurrency(target)} colorClass="text-gray-500" />
        <Metric
          label={'Remaining'}
          value={actual === 0 ? '— —' : formatCurrency(target - actual)}
          colorClass="text-gray-500"
        />
      </div>
    </Section>
  );
}

function EffectivePriceSectionV2({
  deliveredPrice,
  expectedPrice
}: {
  deliveredPrice: number;
  expectedPrice: number;
}) {
  const remainingPrice = expectedPrice - deliveredPrice;

  return (
    <Section>
      <div className="text-lg font-semibold text-gray-700">Effective Price</div>
      <div className="flex flex-col gap-0.5">
        <Metric
          label="Delivered"
          value={deliveredPrice === 0 ? '— —' : formatCurrency(deliveredPrice)}
          colorClass="text-gray-800"
        />
        <Metric label="Expected" value={formatCurrency(expectedPrice)} colorClass="text-gray-500" />
        <Metric
          label="Remaining"
          value={remainingPrice === 0 ? '— —' : formatCurrency(remainingPrice)}
          colorClass="text-gray-500"
        />
      </div>
    </Section>
  );
}

function UnitCostSection({
  actual,
  unitPriceType,
  target
}: {
  actual: number;
  target: number;
  unitPriceType: UnitPriceType;
}) {
  return (
    <Section>
      <div className="text-lg font-semibold text-gray-700">Unit Cost</div>
      <div className="flex flex-col gap-0.5">
        <Metric
          label={`Actual ${unitPriceType.name}`}
          value={formatCurrency(unitPriceType.name === 'CPM' ? actual * 1000 : actual)}
          colorClass="text-gray-800"
        />
        <Metric
          label={`Target ${unitPriceType.name}`}
          value={formatCurrency(unitPriceType.name === 'CPM' ? target * 1000 : target)}
          colorClass="text-gray-500"
        />
      </div>
    </Section>
  );
}

function Section({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-col gap-2 rounded-sm bg-white px-2 py-1.5">{children}</div>;
}

function SectionHeader({ percentage, label }: { percentage: number; label: string }) {
  return (
    <div className="flex items-center justify-between text-gray-700">
      <div>
        <span className="mr-1 text-xl font-bold">
          {percentage === 0 ? '— —%' : `${Math.round(percentage).toLocaleString()}%`}
        </span>
        {label}
      </div>
    </div>
  );
}

function BarRow({ children }: { children: React.ReactNode }) {
  return <div className="flex items-center gap-1">{children}</div>;
}

function Bar({ percentage }: { percentage: number }) {
  return percentage > 100 ? (
    <OverallocatedBar percentage={percentage} />
  ) : (
    <AllocatedBar percentage={percentage} />
  );
}

function OverallocatedBar({ percentage }: { percentage: number }) {
  const overage = ((percentage - 100) / percentage) * 100;
  return (
    <div className="flex h-4 flex-1">
      <div className="rounded-l-xl bg-gray-500" style={{ width: `${100 - overage}%` }} />
      <div
        className="progress-bar-over-allocated-shadow bg-linear-to-r rounded-r-xl from-[#FBBF24] to-[#CA8A04]"
        style={{ width: `${overage}%` }}
      />
    </div>
  );
}

function AllocatedBar({ percentage }: { percentage: number }) {
  return (
    <div className="flex h-4 flex-1">
      <div className="rounded-xl bg-gray-500" style={{ width: `${percentage}%` }} />
    </div>
  );
}

function Metric({
  label,
  value,
  colorClass
}: {
  label: string;
  value: string | number;
  colorClass: string;
}) {
  return (
    <div className="flex items-center justify-between">
      <div className="text-xs text-gray-600">{label}</div>
      <div className={clsx('text-sm font-semibold', colorClass)}>{value}</div>
    </div>
  );
}

function calcElapsedProportion(
  startDate: UTCDate | null | undefined,
  endDate: UTCDate | null | undefined
): number {
  if (!startDate || !endDate) return 0;

  const currentDate = UTCYesterday();
  // Ensure the currentDate is between startDate and endDate

  if (isBefore(currentDate, startDate)) return 0;
  if (isAfter(currentDate, endDate)) return 100;

  const totalDuration = diffInDays(startDate, endDate);
  const elapsedDuration = diffInDays(startDate, currentDate);

  // Calculate the percentage elapsed
  return calcProportion(elapsedDuration, totalDuration);
}

function calcProportion(allocated: number | null | undefined, total: number) {
  if (isNil(allocated) || total === 0) return 0;
  return (allocated / total) * 100;
}
